import { useEffect, useRef, useState } from 'react'
import { PageHeader } from '../../components/PageHeader'
import { BackgroundTitle } from '../../components/BackgroundTitle'
import { readError } from '../../lib/errors'
import { useT } from '../../i18n'
import {
  closeenoughGuess, closeenoughHint, fetchCloseenoughToday,
  type CloseToday,
} from '../../lib/api/wordGames'
import { WordWeeklyChart } from './WordWeeklyChart'
import { HowToOverlay } from './HowToOverlay'

const TITLE = 'CLOSE ENOUGH'
const ACCENT = '#A8A020'
const BORDER = '#D8D844'

const HOW_TO = [
  'One secret word a day. Guess any word and see how close it lands in meaning.',
  'Common words only, no proper nouns. Places and names are not in the dictionary.',
  '1 is the word itself. Low numbers are close and show green, high numbers drift to red, and cold means the meaning is far away.',
  'Start with 600 points. Every guess after the first costs 5.',
  'Three hints a day, each one closer than the last. Every hint costs 50 points.',
  'Daily scores add up Monday through Sunday. Top of the chart wins the week and a stamp.',
]

interface Props {
  onBack: () => void
}

/**
 * Warmth badge for a meaning-rank out of the 30k-word space. LOW rank
 * means CLOSE, so the scale runs green (close) through gold and
 * orange out to red, and past the precomputed top 3000 it is simply
 * cold, the reddest of all (owner correction 2026-08-11: green must
 * mean close, red must mean far).
 */
function warmth(rank: number | null): { label: string; className: string } {
  if (rank === null) return { label: 'cold', className: 'bg-[#C62828] text-white' }
  if (rank === 1) return { label: '1', className: 'bg-brand-success text-white' }
  if (rank <= 10) return { label: `${rank}`, className: 'bg-[#2E7D32] text-white' }
  if (rank <= 100) return { label: `${rank}`, className: 'bg-[#0EAE93] text-white' }
  if (rank <= 500) return { label: `${rank}`, className: 'bg-brand-lime text-brand-text' }
  if (rank <= 1500) return { label: `${rank}`, className: 'bg-[#F2A93A] text-white' }
  return { label: `${rank}`, className: 'bg-[#D4603A] text-white' }
}

/**
 * The daily meaning hunt: guess words, each comes back with how close
 * it sits to the secret word's meaning, fewer guesses scores higher.
 * Ranks come from a precomputed table server-side; the client never
 * holds anything to spoil.
 */
export function CloseEnoughScreen({ onBack }: Props) {
  const t = useT()
  const [state, setState] = useState<CloseToday | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [guessError, setGuessError] = useState<string | null>(null)
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [lastGuess, setLastGuess] = useState<{ guess: string; rank: number | null; isHint?: boolean } | null>(null)
  const [showHowTo, setShowHowTo] = useState(false)
  const [chartRefresh, setChartRefresh] = useState(0)
  const inputRef = useRef<HTMLInputElement | null>(null)

  /** Game day of the board on screen; a change clears yesterday's leftovers. */
  const dayRef = useRef<string | null>(null)

  async function refresh() {
    try {
      const fresh = await fetchCloseenoughToday()
      if (dayRef.current !== null && dayRef.current !== fresh.day) {
        // Midnight passed (Mountain time) while this board was open.
        // Yesterday stays unsolved at 0; today's board takes over.
        setLastGuess(null)
        setInput('')
        setGuessError(null)
      }
      dayRef.current = fresh.day
      setState(fresh)
    } catch (e) {
      setError(readError(e))
    }
  }

  useEffect(() => { void refresh() }, [])

  // Coming back to the foreground (the app slept overnight on this
  // screen) re-reads the server so a new day replaces yesterday's board.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function guess() {
    const word = input.trim().toLowerCase()
    if (!word || busy || !state || state.solved) return
    setBusy(true)
    setGuessError(null)
    try {
      const r = await closeenoughGuess(word)
      setInput('')
      // Refresh first: if the day rolled, this guess already landed on
      // today's board and must survive the day-change reset.
      await refresh()
      if (!r.repeat || !r.solved) setLastGuess({ guess: word, rank: r.rank })
      if (r.solved) setChartRefresh(n => n + 1)
      inputRef.current?.focus()
    } catch (e) {
      setGuessError(readError(e))
    } finally {
      setBusy(false)
    }
  }

  async function hint() {
    if (busy || !state || state.solved) return
    setBusy(true)
    setGuessError(null)
    try {
      const h = await closeenoughHint()
      await refresh()
      setLastGuess({ guess: h.word, rank: h.rank, isHint: true })
    } catch (e) {
      setGuessError(readError(e))
    } finally {
      setBusy(false)
    }
  }

  const solved = state?.solved ?? false

  return (
    <div className="screen">
      <BackgroundTitle text={TITLE} color="#C9C930" />
      <PageHeader
        title={TITLE}
        subtitle={t('Guess your way to the secret word by meaning')}
        onBack={onBack}
      />

      <div className="flex-1 overflow-y-auto px-5 pb-8 relative z-10">
        <button
          type="button"
          onClick={() => setShowHowTo(true)}
          className="w-full mb-3 py-2.5 rounded-full border-2 border-brand-border bg-white
                     text-xs font-semibold uppercase tracking-widest text-brand-muted
                     active:scale-[0.98] transition-transform"
        >
          {t('How to play')}
        </button>

        {error && (
          <div className="card p-3 bg-brand-danger/10 border border-brand-danger/30 mb-3">
            <p className="text-sm text-brand-danger">{error}</p>
          </div>
        )}

        {!state && !error && (
          <div className="h-40 rounded-2xl bg-brand-elevated animate-pulse" />
        )}

        {state && !state.available && (
          <p className="text-sm text-brand-muted text-center py-10">
            {t('No secret word today. Come back tomorrow.')}
          </p>
        )}

        {state?.available && (
          <>
            {solved ? (
              <div className="card p-4 mt-1 rounded-2xl text-center border-2"
                   style={{ borderColor: BORDER, backgroundColor: '#F6F6DC' }}>
                <p className="text-[10px] font-semibold uppercase tracking-widest text-brand-muted">
                  {state.guesses === 1
                    ? t('Solved in 1 guess')
                    : t('Solved in {n} guesses', { n: state.guesses })}
                  {state.hints > 0
                    ? (state.hints === 1 ? t(' · 1 hint') : t(' · {n} hints', { n: state.hints }))
                    : ''}
                </p>
                <p className="font-display font-bold text-4xl uppercase tracking-wide mt-1"
                   style={{ color: ACCENT }}>
                  {state.secret}
                </p>
                <p className="font-display font-bold text-xl text-brand-text mt-1">
                  {t('{n} pts', { n: state.score ?? 0 })}
                </p>
              </div>
            ) : (
              <>
                <div className="flex gap-2 mt-1">
                  <input
                    ref={inputRef}
                    value={input}
                    onChange={e => { setInput(e.target.value); setGuessError(null) }}
                    onKeyDown={e => { if (e.key === 'Enter') void guess() }}
                    placeholder={t('Type a word')}
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    className="flex-1 min-w-0 rounded-2xl border-2 border-brand-border bg-white
                               px-4 py-3 text-base outline-hidden focus:border-brand-text"
                  />
                  <button
                    onClick={() => void guess()}
                    disabled={!input.trim() || busy}
                    className="px-5 rounded-2xl font-display font-bold text-brand-text
                               active:scale-[0.98] transition-transform disabled:opacity-40"
                    style={{ backgroundColor: BORDER }}
                  >
                    {t('Guess')}
                  </button>
                </div>
                <button
                  onClick={() => void hint()}
                  disabled={busy || (state.hints ?? 0) >= 3}
                  className="w-full mt-2 py-2.5 rounded-2xl border-2 text-sm font-semibold
                             active:scale-[0.98] transition-transform disabled:opacity-40"
                  style={{ borderColor: BORDER, color: ACCENT }}
                >
                  {(state.hints ?? 0) >= 3
                    ? t('No hints left today')
                    : t('Hint · 50 pts each · {n} left today', { n: 3 - (state.hints ?? 0) })}
                </button>
                {guessError && (
                  <p className="text-xs text-brand-danger mt-2">{guessError}</p>
                )}
                {lastGuess && (
                  <div className="flex items-center gap-3 mt-3 rounded-2xl px-4 py-3 border-2"
                       style={{ borderColor: BORDER, backgroundColor: '#F6F6DC' }}>
                    <span className="flex-1 font-semibold text-brand-text">
                      {lastGuess.guess}
                      {lastGuess.isHint && (
                        <span className="ml-2 text-[10px] font-semibold uppercase tracking-widest text-brand-muted">
                          {t('hint')}
                        </span>
                      )}
                    </span>
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${warmth(lastGuess.rank).className}`}>
                      {warmth(lastGuess.rank).label === 'cold' ? t('cold') : warmth(lastGuess.rank).label}
                    </span>
                  </div>
                )}
                <p className="text-[10px] font-semibold uppercase tracking-widest text-brand-muted mt-4 mb-2">
                  {state.guesses === 1 ? t('1 guess') : t('{n} guesses', { n: state.guesses })}
                  {state.hints > 0
                    ? (state.hints === 1 ? t(' · 1 hint') : t(' · {n} hints', { n: state.hints }))
                    : ''}
                  {t(' · closest first')}
                </p>
              </>
            )}

            <div className="space-y-1.5 mt-2">
              {state.list.map(g => (
                <div key={g.guess}
                     className="flex items-center gap-3 bg-white border border-brand-border
                                rounded-2xl px-4 py-2.5">
                  <span className="flex-1 min-w-0 truncate text-sm font-semibold text-brand-text">
                    {g.guess}
                    {g.isHint && (
                      <span className="ml-2 text-[10px] font-semibold uppercase tracking-widest text-brand-muted">
                        {t('hint')}
                      </span>
                    )}
                  </span>
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${warmth(g.rank).className}`}>
                    {warmth(g.rank).label === 'cold' ? t('cold') : warmth(g.rank).label}
                  </span>
                </div>
              ))}
            </div>

            {solved && (
              <WordWeeklyChart game="close_enough" refresh={chartRefresh}
                               accentClass="text-game-closeenough-dark" />
            )}
          </>
        )}
      </div>

      {showHowTo && (
        <HowToOverlay title={TITLE} lines={HOW_TO.map(l => t(l))} accent={ACCENT} image="/games/close-enough.webp"
                      onClose={() => setShowHowTo(false)} />
      )}
    </div>
  )
}
